import { on } from '@ember/modifier';
import { or } from 'ember-truth-helpers';
import { service } from '@ember/service';
import { sortConversationMembers } from '../../utils/messaging/conversation';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import AttachTooltip from 'ember-attacher/components/attach-tooltip';
import BaseRoute from './base-route.gjs';
import Component from '@glimmer/component';
import ConfirmationModal from '../confirmation-modal.gjs';
import ConversationDisplayName from '../conversation-display-name.gjs';
import ConversationMemberListItem from '../conversation-member-list-item.gjs';
import GhostButton from 'ember-simplepractice/components/spds/action/button/ghost';
import NegativeButton from 'ember-simplepractice/components/spds/action/button/negative';
import SpdsFeedbackAlertBannerMessage from 'ember-simplepractice/components/spds/feedback/alert-banner/message';
import styles from './conversation-details.module.scss';
import svgJar from 'ember-svg-jar/helpers/svg-jar';

export default class MessagingRoutesConversationDetails extends Component {
  @service intl;
  @service messaging;
  @service messagingNavigation;

  @tracked confirmSettings = null;

  get routeHandler() {
    return this.args.handler;
  }

  get conversation() {
    return this.routeHandler.conversation;
  }

  get isDraft() {
    return this.routeHandler.isDraft;
  }

  get showInactiveBanner() {
    return (
      !this.isDraft &&
      this.conversation.isInactiveSoleRemainingGroup(this.messaging.currentProfile?.user)
    );
  }

  get displayName() {
    return this.conversation.displayName;
  }

  get isClinician() {
    return this.messaging.currentProfile.isClinician;
  }

  get canManageGroupMembers() {
    return this.isClinician && this.conversation.isGroup;
  }

  get showModifyMembers() {
    return this.canManageGroupMembers && this.isDraft;
  }

  get showAddTeamMembers() {
    return this.canManageGroupMembers && !this.isDraft;
  }

  get collapsedMembers() {
    return sortConversationMembers(
      this.conversation.collapsedMembers,
      this.messaging.currentProfile?.user
    );
  }

  get showViewPreviousMembers() {
    return this.messaging.currentProfile?.isClinician && this.routeHandler.hasPreviousMembers;
  }

  get isConfirmOpen() {
    return this.confirmSettings !== null;
  }

  onNameClickFor = member => {
    const profileMember = member.isCouple ? member.members?.[0] : member;
    if (!this.routeHandler.canNavigateToClientProfile(profileMember)) {
      return undefined;
    }

    return () => {
      document.activeElement.blur();
      this.routeHandler.navigateToClientProfile.perform(profileMember);
    };
  };

  goBack = async () => {
    await this.messagingNavigation.leaveChildRoute();
  };

  viewPreviousMembers = async () => {
    await this.messagingNavigation.toPreviousMembers();
  };

  onModifyMembers = () => {};

  onAddTeamMembers = () => {
    this.messagingNavigation.toAddMembers();
  };

  closeConfirm = () => {
    this.confirmSettings = null;
  };

  openLeaveConfirm = () => {
    if (this.routeHandler.isLeaveDisabled) return;
    this.confirmSettings = {
      title: this.intl.t('emc.conversation_details.leave_confirm_question'),
      body: this.intl.t('emc.conversation_details.leave_confirm_body'),
      confirmLabel: this.intl.t('emc.conversation_details.leave_conversation'),
      onConfirm: () => this.routeHandler.leaveConversation(),
    };
  };

  openDiscardConfirm = () => {
    this.confirmSettings = {
      title: this.intl.t('emc.conversation_details.discard_confirm_question'),
      body: this.intl.t('emc.conversation_details.discard_confirm_body'),
      confirmLabel: this.intl.t('emc.conversation_details.discard_draft'),
      onConfirm: () => this.routeHandler.discardDraft(),
    };
  };

  <template>
    {{#if this.isConfirmOpen}}
      <ConfirmationModal
        @title={{this.confirmSettings.title}}
        @body={{this.confirmSettings.body}}
        @confirmLabel={{this.confirmSettings.confirmLabel}}
        @onCancel={{this.closeConfirm}}
        @onConfirm={{this.confirmSettings.onConfirm}}
      />
    {{else}}
      <BaseRoute
        class="{{styles.component}} conversation-details-page"
        data-test-conversation-details-route
      >
        <:header>
          <GhostButton
            @fitContent={{true}}
            @iconOnly={{true}}
            {{on "click" this.goBack}}
            class="back-button"
            data-test-conversation-details-page-back-button
          >
            {{svgJar "messaging-chevron-left"}}
          </GhostButton>

          <span class="title">{{t "emc.conversation_details.title"}}</span>
        </:header>
        <:default>
          {{! Draft wins when both apply. Sole-member inactive is for published conversations. }}
          {{#if this.isDraft}}
            <div class="details-banner" data-test-conversation-details-draft-banner>
              <SpdsFeedbackAlertBannerMessage
                @type="info"
                @header={{t "emc.draft_conversation_banner.title"}}
                @body={{t "emc.draft_conversation_banner.description"}}
                @includeIcon={{false}}
              />
            </div>
          {{else if this.showInactiveBanner}}
            <div class="details-banner" data-test-conversation-details-inactive-banner>
              <SpdsFeedbackAlertBannerMessage
                @type="info"
                @header={{t "emc.inactive_conversation.banner_title"}}
                @body={{t "emc.inactive_conversation.banner_body"}}
                @includeIcon={{false}}
              />
            </div>
          {{/if}}

          <ConversationDisplayName
            @displayName={{this.displayName}}
            @editable={{this.isClinician}}
            @onSave={{this.routeHandler.updateDisplayName}}
          />

          <div class="members-section-header" data-test-members-section-header>
            <h2 class="section-title" data-test-members-section-title>
              {{t "emc.conversation_details.members_section" count=this.collapsedMembers.length}}
            </h2>

            {{#if this.showModifyMembers}}
              <GhostButton
                @size="small"
                class="modify-members-button"
                {{on "click" this.onModifyMembers}}
                data-test-modify-members
              >
                {{svgJar "messaging-users"}}
                {{t "emc.draft_conversation_banner.modify_members"}}
              </GhostButton>
            {{else if this.showAddTeamMembers}}
              <GhostButton
                @size="small"
                class="modify-members-button"
                {{on "click" this.onAddTeamMembers}}
                data-test-add-team-members
              >
                {{svgJar "messaging-users"}}
                {{t "emc.add_team_members.cta"}}
              </GhostButton>
            {{/if}}
          </div>

          <ul class="members-list" data-test-members-list role="list">
            {{#each this.collapsedMembers key="id" as |member|}}
              <ConversationMemberListItem
                @member={{member}}
                @onNameClick={{this.onNameClickFor member}}
              />
            {{/each}}

            {{#if this.showViewPreviousMembers}}
              <li class="view-previous-members">
                <button
                  type="button"
                  data-test-view-previous-members
                  {{on "click" this.viewPreviousMembers}}
                >
                  <span class="view-previous-members-label">
                    {{t "emc.conversation_details.view_previous_members"}}
                  </span>
                  <span class="view-previous-members-chevron" aria-hidden="true">
                    {{svgJar "messaging-chevron-right"}}
                  </span>
                </button>
              </li>
            {{/if}}
          </ul>

          {{#if (or this.routeHandler.showLeaveButton this.isDraft)}}
            <div class="details-action-container" data-test-details-action-container>
              <div class="details-action-wrapper">
                {{#if this.routeHandler.showLeaveButton}}
                  <NegativeButton
                    @fitContentWidth={{true}}
                    @disabled={{this.routeHandler.isLeaveDisabled}}
                    {{on "click" this.openLeaveConfirm}}
                    class="details-action-button leave-conversation-button"
                    data-test-leave-conversation-button
                  >
                    {{t "emc.conversation_details.leave_conversation"}}
                  </NegativeButton>
                  {{#if this.routeHandler.isLeaveDisabled}}
                    <AttachTooltip
                      @placement="bottom"
                      @arrow={{true}}
                      @animation="fade"
                      @class="emc-tooltip leave-disabled-tooltip"
                      @renderInPlace={{true}}
                    >
                      {{t "emc.conversation_details.leave_conversation_disabled_tooltip"}}
                    </AttachTooltip>
                  {{/if}}
                {{else}}
                  <NegativeButton
                    @fitContentWidth={{true}}
                    {{on "click" this.openDiscardConfirm}}
                    class="details-action-button discard-draft-button"
                    data-test-discard-draft-button
                  >
                    {{t "emc.conversation_details.discard_draft"}}
                  </NegativeButton>
                {{/if}}
              </div>
            </div>
          {{/if}}
        </:default>
      </BaseRoute>
    {{/if}}
  </template>
}
